<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, onMounted, ref } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const store = useStore();
const router = useRouter();
const account = useAccount();

const isRefreshing = ref(false);
const isSubmitting = ref(false);

const currentAccount = computed(() => account.currentAccount.value || {});
const customAttributes = computed(
  () => currentAccount.value.custom_attributes || {}
);
const deletionDate = computed(
  () => customAttributes.value.marked_for_deletion_at
);
const deletionReason = computed(
  () => customAttributes.value.marked_for_deletion_reason || 'manual_deletion'
);
const isMarkedForDeletion = computed(() => !!deletionDate.value);

const deleteAccountRoute = computed(() =>
  account.accountScopedRoute('deletar_conta_index')
);

const formattedDeletionDate = computed(() => {
  if (!deletionDate.value) {
    return '';
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'full',
    timeStyle: 'short',
  }).format(new Date(deletionDate.value));
});

const syncAccount = async () => {
  isRefreshing.value = true;
  try {
    await store.dispatch('accounts/get', { silent: true });
  } finally {
    isRefreshing.value = false;
  }
};

const clearDeletionMark = async () => {
  isSubmitting.value = true;
  try {
    await store.dispatch('accounts/toggleDeletion', {
      action_type: 'undelete',
    });
    await syncAccount();
    useAlert('A exclusão agendada foi cancelada com sucesso.');
    router.push(deleteAccountRoute.value);
  } catch {
    useAlert('Não foi possível cancelar a exclusão agendada agora.');
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(() => {
  syncAccount();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <section class="flex h-full flex-col overflow-hidden bg-[#fcfaf6]">
    <LaisHeader
      title="Exclusão agendada"
      helper="A conta foi marcada para exclusão e ainda pode ser recuperada dentro do prazo."
    />

    <div class="flex-1 overflow-auto px-4 py-4 sm:px-6 lg:px-10 lg:py-6">
      <div
        class="grid gap-5 xl:grid-cols-[minmax(0,1.1fr)_minmax(320px,0.9fr)]"
      >
        <div class="grid gap-5">
          <LaisCard title="Status da solicitação">
            <div
              v-if="isMarkedForDeletion"
              class="space-y-4 text-[13px] leading-6 text-lais-text-muted"
            >
              <div
                class="rounded-lais border border-[#f1c9cf] bg-[#fff5f6] px-4 py-4 text-[#912018]"
              >
                <p class="text-[15px] font-semibold">
                  Conta {{ currentAccount.name || 'Imudei' }} marcada para
                  exclusão
                </p>
                <p class="mt-1">
                  Data prevista:
                  <strong>{{ formattedDeletionDate }}</strong>
                </p>
                <p class="mt-1">
                  Origem da solicitação:
                  <strong>{{
                    deletionReason === 'manual_deletion'
                      ? 'Solicitação manual do administrador'
                      : 'Regra automática da plataforma'
                  }}</strong>
                </p>
              </div>

              <p>
                Até a data acima, você ainda pode cancelar a solicitação e
                seguir usando a conta normalmente.
              </p>

              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 rounded-lais border border-[#f1c9cf] bg-white px-4 py-2.5 text-[14px] font-semibold text-[#b42318] transition-all hover:bg-[#fff5f6] disabled:cursor-not-allowed disabled:opacity-60"
                  :disabled="isSubmitting || isRefreshing"
                  @click="clearDeletionMark"
                >
                  <span class="i-lucide-rotate-ccw size-4" />
                  Cancelar exclusão agendada
                </button>

                <RouterLink
                  :to="account.accountScopedRoute('meus_dados_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-2.5 text-[14px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-arrow-left size-4" />
                  Voltar para meus dados
                </RouterLink>
              </div>
            </div>

            <div
              v-else
              class="space-y-4 text-[13px] leading-6 text-lais-text-muted"
            >
              <p>
                Não encontramos uma exclusão agendada para esta conta neste
                momento.
              </p>
              <div class="flex flex-wrap gap-2">
                <LaisPrimaryButton
                  icon="i-lucide-trash-2"
                  @click="router.push(deleteAccountRoute)"
                >
                  Abrir página de exclusão
                </LaisPrimaryButton>
                <RouterLink
                  :to="account.accountScopedRoute('meus_dados_index')"
                  class="inline-flex items-center gap-2 rounded-lais border border-lais-border bg-white px-4 py-2.5 text-[14px] font-semibold text-lais-text transition-all hover:border-lais-primary hover:text-lais-link"
                >
                  <span class="i-lucide-user-round-cog size-4" />
                  Ir para meus dados
                </RouterLink>
              </div>
            </div>
          </LaisCard>
        </div>

        <div class="grid gap-5">
          <LaisCard title="Próximos passos">
            <ol class="space-y-3 text-[13px] leading-6 text-lais-text-muted">
              <li>
                1. Confirme se exportações, contratos e integrações relevantes
                já foram tratadas.
              </li>
              <li>
                2. Use esta mesma tela para cancelar a exclusão enquanto o prazo
                ainda estiver aberto.
              </li>
              <li>
                3. Se a decisão for definitiva, apenas acompanhe o prazo até a
                remoção final da conta.
              </li>
            </ol>
          </LaisCard>
        </div>
      </div>
    </div>
  </section>
</template>
